<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { blockUserAction } from '@/api/functions/users/block-user-action'
import { deleteUserAction } from '@/api/functions/users/delete-user-action'
import { impersonateUserAction } from '@/api/functions/users/impersonate-user-action'
import { unblockUserAction } from '@/api/functions/users/unblock-user-action'
import { useMutationApi } from '@/composables/useApi'
import { hasPermission } from '@/composables/useAuth'
import { useMobileNumber } from '@/composables/useMobileNumber'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { USERS_LIST_SCROLL_KEY } from '../../../constants/user-list.constant'

const props = defineProps({
  user: { type: Object },
})

const emit = defineEmits(['confirmUser'])

const toast = useToast()
const router = useRouter()

const isBlocked = ref(props.user?.blocked === true)

const showDeleteModal = ref(false)
const showBlockModal = ref(false)
const userId = computed(() => props.user?.uuid)

// DELETE  USER
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteUserAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'کاربر با موفقیت حذف شد')
    emit('confirmUser')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteUser = () => {
  if (!userId.value) return
  deleteMutation.mutate(userId.value)
}

// BLOCK USER
const blockMutation = useMutationApi({
  mutationFn: (id: string) => blockUserAction(id),
  onSuccess: (response) => {
    isBlocked.value = true
    toast.success(response?.meta?.message || 'کاربر با موفقیت مسدود شد')
    emit('confirmUser')
    showBlockModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})
// UNBLOCK USER
const unblockMutation = useMutationApi({
  mutationFn: (id: string) => unblockUserAction(id),
  onSuccess: (response) => {
    isBlocked.value = false
    toast.success(response?.meta?.message || 'کاربر با موفقیت رفع مسدود شد')
    emit('confirmUser')
    showBlockModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleBlockUser = () => {
  if (!userId.value) return
  if (isBlocked.value) {
    unblockMutation.mutate(userId.value)
    return
  }
  blockMutation.mutate(userId.value)
}

const goToUserDetails = () => {
  if (!userId.value) return
  sessionStorage.setItem(USERS_LIST_SCROLL_KEY, String(userId.value))
  router.push({
    path: `/users/${userId.value}`,
  })
}
const mobile = computed(() => props.user?.mobile)
const countryCode = computed(() => props.user?.countryCode)
const { whatsappLink, copyMobileNumber } = useMobileNumber(mobile, countryCode)
const impersonateMutation = useMutationApi({
  mutationFn: (id: string) => impersonateUserAction(id),
  onSuccess: (response) => {
    const redirectUrl = (response?.data as any)?.redirect_url
    if (redirectUrl) {
      window.open(redirectUrl, '_blank')
    } else {
      toast.error('لینک ورود دریافت نشد', 'خطا')
    }
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isImpersonating = computed(() => impersonateMutation.isPending?.value ?? false)

const canImpersonate = computed(() => hasPermission('impersonate-users'))

const handleImpersonateUser = () => {
  if (!userId.value) return
  impersonateMutation.mutate(userId.value)
}
</script>

<template>
  <div
  :id="`user-${user?.uuid}`"
  class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">

    <ConfirmModal v-model="showDeleteModal" title="حذف کاربر" :message="'آیا از حذف این کاربر اطمینان دارید؟'"
      confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteUser" @cancel="showDeleteModal = false" />

    <ConfirmModal v-model="showBlockModal" :title="isBlocked ? 'رفع مسدودی کاربر' : 'مسدودسازی کاربر'" :message="isBlocked
      ? 'آیا از رفع مسدودی این کاربر اطمینان دارید؟'
      : 'آیا از مسدودسازی این کاربر اطمینان دارید؟'
      " :confirm-text="isBlocked ? 'بله رفع شود' : 'بله مسدود شود'" cancel-text="بازگشت" @confirm="handleBlockUser"
      @cancel="showBlockModal = false" />

    <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
      <div class=" w-full lg:w-fit flex  items-center">
        <Button v-if="userId && user?.status === 'VERIFIED' && canImpersonate" @click="handleImpersonateUser" :disabled="isImpersonating"
          class="bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2">
          <span class="flex items-center justify-center gap-2 text-[#2d5396]">
            {{ isImpersonating ? 'در حال ورود...' : 'ورود به حساب کاربر' }}
          </span>
        </Button>
        <Button @click="goToUserDetails" :isAdmin="true"
          class="w-1/2 lg:w-auto  rounded-[10px]! px-5 h-10! py-0 text-[13px] font-normal me-2">
          مشاهده
        </Button>
        <Button @click="showDeleteModal = true"
          class=" bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2">
          <span class="flex items-center justify-center gap-2 text-[#2d5396]">
            حذف کاربر
          </span>
        </Button>
        <Button @click="showBlockModal = true"
          class=" bg-transparent w-1/2 lg:w-auto border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal me-2">
          <span class="flex items-center justify-center gap-2 text-[#2d5396]">
            {{ isBlocked ? "رفع مسدودی کاربر" : "مسدودسازی کاربر" }}
          </span>
        </Button>
      </div>
    </div>

    <div class="flex flex-col gap-6  text-black text-sm font-medium">
      <!-- Header -->
      <div class="flex flex-col lg:flex-row items-center justify-center gap-2 lg:justify-start">
        <div
          class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#d5ddea] text-[#2d5396]  font-medium h-10 rounded-[10px] px-6">
          کد کاربری
          <span class="font-fa mx-1">{{ user?.code }}</span>
          <span class="mx-2">|</span>
          <span>
            {{ user?.status === 'VERIFIED' ? 'تایید شده' :
              user?.status === 'EMAIL_VERIFICATION_PENDING' ? 'ثبت نام اولیه' :
                user?.status === 'MOBILE_REGISTRATION_PENDING' ? ' در انتظار ثبت شماره موبایل' :
                  user?.status === 'IDENTIFICATION_REGISTRATION_PENDING' ? ' در انتظار ارسال مدارک هویتی' :
                    user?.status === 'IDENTIFICATION_VERIFICATION_PENDING' ? ' در انتظار تایید مدارک هویتی' :
                      user?.status === 'MOBILE_VERIFICATION_PENDING' ? ' در انتظار تایید شماره موبایل' :
                        'منقضی شده' }}
          </span>
        </div>

      </div>
      <!-- Body -->
      <div class="flex flex-col gap-2.5 text-sm text-black font-medium">
        <div class="flex items-center gap-1">
          <span>نام و نام خانوادگی:</span>

          <span class="">{{ user?.fullName }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>شماره موبایل:</span>

          <template v-if="user?.mobile">
            <a :href="whatsappLink" target="_blank" rel="noopener noreferrer"
              class="font-fa text-[#2d5396] hover:underline">
              {{ user?.countryCode }}{{ user?.mobile }}+
            </a>

            <button type="button" @click="copyMobileNumber" class="p-1 hover:bg-gray-100 rounded-md transition-colors"
              title="کپی شماره">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          </template>
          <span v-else>–</span>
        </div>
        <div class="flex items-center gap-1">
          <span>ایمیل:</span>
          <span>{{ user?.email }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>کانال ورودی:</span>
          <span>{{ user?.referralType === 'DIRECT' ? 'مستقیم' : 'ریفرال' }}</span>
        </div>

      </div>
      <!-- Footer -->
      <div
        class="flex flex-col lg:flex-row lg:items-center gap-6 mt-2 lg:mt-4 lg:gap-0   pe-1 text-sm text-[#1b325a] font-normal"
        :class="user?.country ?
          'justify-between' : 'justify-end'">
        <!-- Country  -->
        <div v-if="user?.country" class="flex items-center gap-1 text-sm font-medium text-[#000000]">
          <img :src="user?.country?.flagUrl" width="24" height="16" alt="flag" />
          {{ user?.country?.title?.fa }}
        </div>

        <div class="flex flex-wrap items-center justify-between lg:justify-end text-sm font-medium gap-4 mb-2">
          <div class="flex items-center">
            ثبت:<span dir="ltr" class="ms-1 font-fa">{{ stringToFaDateTime(user?.createdAt) }}</span>
          </div>

        </div>
      </div>

    </div>
  </div>

</template>
